/**
 * `decide-contract-type` — the Admin's decision on a contract's type · CLM-110
 * (FR-35, FR-34, FR-36, DR-10, CR-08; DR-01 as amended by D-110-1). The
 * decisions in force are D-110-1 and D-110-2 (Harnoor Gandhi, 1 Oct 2026, Jira
 * CLM-110 comments 1942023 and 1942124); the refinements R-110-9 … R-110-28 are
 * the spec's (`phase4-specs/CLM-110-ai-decided-contract-type.md`).
 *
 * `POST /v1/contracts/{contract_key}/type`, gate `admin:configure` (D-110-2:
 * only an Admin accepts or corrects a type — a governance act with its own audit
 * row). Option B of the spec's §3.2: a dedicated action, never a
 * `field_verification` row, so no contributor ever sees an action they cannot
 * take and the queue, `data_trust` and CLM-108's tables are untouched.
 *
 * ## What one decision does (§3.5)
 *
 * The unit is the **agreement family**: the decided root and every contract with
 * its `root_contract_key`. One audited UPDATE sets the type, `confirmed` and the
 * decision row on every family row — `fk_contract_amends_type` makes any other
 * write unrepresentable (R-110-15). The root's source follows R-110-20 (the AI's
 * type taken → `ai_proposed`; the provisional type kept → unchanged; any other
 * → `manual`); every amendment's becomes `inherited`. Into SOW the request must
 * carry the number; out of SOW the numbers are cleared and listed. **The key
 * never changes** (FR-35), and the notification ledger is untouched (rule 15).
 *
 * ## What is counted for accuracy (R-110-18, R-110-28)
 *
 * Only an agreement's **first** decision after its own latest AI reading — a
 * proposal, or an answer outside the vocabulary — is a counted human decision in
 * CLM-107's `vw_field_accuracy_decision`, under `CONTRACT_TYPE_PROPOSAL_FIELD`
 * (`classification.contract_type`), with no view change. The decision's
 * `HumanVerification` row then carries `seen_extracted_value` and the reading's
 * schema version; otherwise it carries neither, and CLM-107 excludes it by
 * construction.
 *
 * ## `seen` is the attestation (R-110-17)
 *
 * The request names what the Admin looked at — the type, the latest reading, the
 * confirmation and every family row the dialog listed — in the
 * `seen_extracted_value` precedent. The server compares it with the rows it
 * locked and answers `409 stale_view` on any difference, so a decision applies
 * only from the state it names: a retried request after a lost response is
 * `stale_view`, and no idempotency key is needed. Without
 * `family_contract_keys`, an amendment filed after the page loaded (with no
 * reading yet) would change nothing else in `seen`, and the decision would retype
 * a row the Admin never saw.
 *
 * ## Scope
 *
 * Every statement is scoped on `root_contract_key` (rules 9, 10). Out of scope
 * is `404 contract_not_found`, never 403. The client sends no UPN, role or scope:
 * the request is `.strict()`, so trying is a validation error.
 */
import { z } from 'zod';
import { CONTRACT_TYPE_LINEAGE_NOTE_KINDS, ContractTypeDecisionSeenSchema } from '../domain/contract.js';
import { ContractKeySchema } from '../primitives.js';
import { AuditReceiptSchema } from '../provenance.js';
import { ContractTypeDecidedBySchema, ContractTypeDecisionKindSchema, ContractTypeProposalSchema, ContractTypeSchema, } from '../vocabulary.js';
/*
 * `ContractTypeDecisionSeenSchema` — what the Admin looked at (R-110-17) — is
 * declared in `domain/contract.ts`, beside the record's `contract_type_status`
 * that carries it too: this module imports that one, so declaring it here would
 * be an import cycle. The package index exports it from there.
 */
export const DecideContractTypeRequestSchema = z
    .object({
    /** Path parameter — the agreement (an amendment is refused: decide its agreement). */
    contract_key: ContractKeySchema,
    /** The type to set: MSA, NDA, SOW or OTHER. `AMENDMENT` is never a decision (R-110-15). */
    contract_type: ContractTypeProposalSchema,
    /** Required into SOW (`sow_number_required`); refused otherwise (`sow_number_not_applicable`). */
    sow_number: z.string().min(1).max(32).optional(),
    /** Optional; recorded in the decision row's `new_value`. */
    note: z.string().min(1).max(500).optional(),
    seen: ContractTypeDecisionSeenSchema,
})
    .strict()
    .refine((request) => request.seen.family_contract_keys.includes(request.contract_key), {
    message: 'seen.family_contract_keys includes the decided contract',
    path: ['seen', 'family_contract_keys'],
});
/**
 * Every refusal, as the first `field_errors` message's `code:` prefix, in the
 * order the planner checks them (§4.5): `404 contract_not_found`; `409
 * amendment_follows_agreement` (the message names the root); `409
 * awaiting_ai_proposal` (an unrestricted root not yet past extraction); `409
 * stale_view`; `400 no_change` (confirmed, the same type, and no reading newer
 * than the decision); `400 sow_number_required` / `422 sow_number_shape` / `400
 * sow_number_not_applicable`; `409 sow_number_in_use` (held outside this family);
 * `409 current_base_contract` (replace the base on the Base MSA page first); `409
 * family_changed` (the UPDATE met a different family than was locked).
 */
export const CONTRACT_TYPE_DECISION_REFUSALS = [
    'contract_not_found',
    'amendment_follows_agreement',
    'awaiting_ai_proposal',
    'stale_view',
    'no_change',
    'sow_number_required',
    'sow_number_shape',
    'sow_number_not_applicable',
    'sow_number_in_use',
    'current_base_contract',
    'family_changed',
];
/**
 * What the decision flagged but did not change (§3.5): the record's three
 * lineage notes, plus `sow_numbers_cleared` (FR-15 will now report a listed
 * number as `missing`), `deviation_reports_kept` (immutable, pinned to the type
 * at run time) and `past_base_nominations` (the list reports `type_mismatch`).
 */
export const CONTRACT_TYPE_DECISION_NOTE_KINDS = [
    ...CONTRACT_TYPE_LINEAGE_NOTE_KINDS,
    'sow_numbers_cleared',
    'deviation_reports_kept',
    'past_base_nominations',
];
export const ContractTypeDecisionNoteKindSchema = z.enum(CONTRACT_TYPE_DECISION_NOTE_KINDS);
export const ContractTypeDecisionFamilyRowSchema = z
    .object({
    contract_key: ContractKeySchema,
    contract_type_before: ContractTypeSchema,
    contract_type_after: ContractTypeProposalSchema,
    sow_number_before: z.string().max(32).nullable(),
    sow_number_after: z.string().max(32).nullable(),
})
    .strict();
export const DecideContractTypeResponseSchema = z
    .object({
    contract_key: ContractKeySchema,
    contract_type: ContractTypeProposalSchema,
    /**
     * The root's source after the decision (R-110-20) — never `inherited`. `null`
     * when the provisional type was kept on a contract recorded before CLM-110:
     * the source is unchanged, and a legacy `NULL` stays `NULL` (no backfill
     * guesses it).
     */
    contract_type_source: ContractTypeDecidedBySchema.nullable(),
    contract_type_confirmation: z.literal('confirmed'),
    decision: z.object({ kind: ContractTypeDecisionKindSchema, counted_for_accuracy: z.boolean() }).strict(),
    /** Every family row the UPDATE wrote, the root first — `contract_key` never changes. */
    family: z.array(ContractTypeDecisionFamilyRowSchema).min(1),
    notes: z.array(z
        .object({
        kind: ContractTypeDecisionNoteKindSchema,
        contract_keys: z.array(ContractKeySchema).min(1),
    })
        .strict()),
    /** The decision's `HumanVerification` row (rule 7) — the NFR-07 receipt. */
    audit: AuditReceiptSchema,
})
    .strict()
    .refine((body) => body.family[0]?.contract_key === body.contract_key, {
    message: 'the family lists the decided root first',
    path: ['family'],
})
    .refine((body) => body.family.every((row) => row.contract_type_after === body.contract_type), {
    message: 'every family row takes the decided type',
    path: ['family'],
})
    .refine((body) => body.contract_type_source !== 'inherited', {
    message: 'the root’s type is decided, never inherited',
    path: ['contract_type_source'],
});
//# sourceMappingURL=decide-contract-type.js.map